<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import UiIcon from '@/components/common/UiIcon.vue'
import { useRouteSeoContent } from '@/composables/useRouteSeoContent'
import { type NoticeModel, type NoticeVariant } from '@/models/notice'
import { useNoticeStore } from '@/stores/notice.store'
import { useLocaleStore } from '@/stores/locale.store'
import { useConsentStore } from '@/stores/consent.store'
import { useUIStore } from '@/stores/ui.store'

const props = withDefaults(defineProps<{
  inline?: boolean
}>(), {
  inline: false,
})

const noticeStore = useNoticeStore()
const localeStore = useLocaleStore()
const consentStore = useConsentStore()
const ui = useUIStore()
const { visibleNotices } = storeToRefs(noticeStore)
const { categories: consentCategories } = storeToRefs(consentStore)
const {
  backendAvailabilityIssue,
  isBackendUnavailable,
  isBackendUnavailableAlertDismissed,
} = storeToRefs(ui)
const { currentRouteContent } = useRouteSeoContent()
const { t } = useI18n()

type RenderedNotice = NoticeModel & {
  source: 'backend' | 'remote'
}

const currentPageId = computed(() => currentRouteContent.value?.id)
const hasCookieConsent = computed(() => consentCategories.value.length > 0)
const activeIndex = ref(0)

const backendUnavailableNotice = computed<RenderedNotice | null>(() => {
  if (!isBackendUnavailable.value || isBackendUnavailableAlertDismissed.value) return null

  return {
    id: 'system:backend-unavailable',
    source: 'backend',
    title: t('system.backendUnavailable.title'),
    message: t('system.backendUnavailable.description'),
    variant: 'warning',
    icon: 'triangle-alert',
    dismissible: false,
    alert: true,
    pageIds: [],
  }
})

const notices = computed<RenderedNotice[]>(() => {
  const pageId = currentPageId.value
  const backendNotice = backendUnavailableNotice.value
  const remoteNotices = hasCookieConsent.value
    ? visibleNotices.value
      .filter((notice) => {
        if (notice.pageIds.length === 0) return true
        return typeof pageId === 'number' && notice.pageIds.includes(pageId)
      })
      .map((notice) => ({
        ...notice,
        source: 'remote' as const,
      }))
    : []

  return backendNotice ? [backendNotice, ...remoteNotices] : remoteNotices
})

const activeNotice = computed(() => notices.value[activeIndex.value] ?? null)
const hasMultipleNotices = computed(() => notices.value.length > 1)
const noticeCountLabel = computed(() =>
  t('notices.count', {
    current: Math.min(activeIndex.value + 1, notices.value.length),
    total: notices.value.length,
  }),
)

function iconName(notice: RenderedNotice) {
  if (notice.icon) return notice.icon

  switch (notice.variant) {
    case 'success':
      return 'circle-check-big'
    case 'danger':
    case 'warning':
      return 'triangle-alert'
    case 'info':
      return 'info'
    default:
      return 'message-circle'
  }
}

function iconClass(variant: NoticeVariant) {
  switch (variant) {
    case 'success':
      return 'bg-success-soft text-success'
    case 'danger':
      return 'bg-error-soft text-error'
    case 'warning':
      return 'bg-warning-soft text-warning'
    case 'info':
      return 'bg-info-soft text-info'
    default:
      return 'bg-neutral-bg text-neutral-deep'
  }
}

function dismiss(notice: RenderedNotice) {
  if (notice.source === 'backend') {
    ui.dismissBackendUnavailableAlert()
    return
  }

  noticeStore.dismiss(notice.id)
}

function showPreviousNotice() {
  if (notices.value.length < 2) return

  activeIndex.value = activeIndex.value === 0
    ? notices.value.length - 1
    : activeIndex.value - 1
}

function showNextNotice() {
  if (notices.value.length < 2) return

  activeIndex.value = (activeIndex.value + 1) % notices.value.length
}

onMounted(() => {
  if (!hasCookieConsent.value) return

  void noticeStore.ensure()
})

watch(
  () => localeStore.currentLocale,
  () => {
    if (!hasCookieConsent.value) return

    void noticeStore.ensure({ force: true })
  },
)

watch(
  hasCookieConsent,
  (hasConsent) => {
    if (!hasConsent) return

    void noticeStore.ensure()
  },
)

watch(
  () => notices.value.map((notice) => notice.id),
  (ids) => {
    if (ids.length === 0) {
      activeIndex.value = 0
      return
    }

    if (activeIndex.value >= ids.length) {
      activeIndex.value = ids.length - 1
    }
  },
)
</script>

<template>
  <section
    v-if="notices.length"
    :class="props.inline
      ? 'w-full max-w-4xl px-4 sm:px-6'
      : 'fixed inset-x-0 bottom-4 z-60 mx-auto flex max-w-4xl flex-col gap-3 px-4 sm:px-6'"
    :aria-live="notices.some((notice) => notice.alert) ? 'assertive' : 'polite'"
    :aria-label="t('notices.region')"
  >
    <div
      v-if="activeNotice"
      :key="activeNotice.id"
      class="notice-wrapper flex flex-col gap-2"
    >
      <div
        v-if="hasMultipleNotices || activeNotice.dismissible"
        class="flex items-center justify-end gap-2"
      >
        <span
          v-if="hasMultipleNotices"
          class="rounded-none px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {{ noticeCountLabel }}
        </span>

        <div
          v-if="hasMultipleNotices"
          class="flex items-center gap-1 rounded-none p-1"
        >
          <button
            type="button"
            class="utility-trigger utility-trigger--icon size-8 rounded-none"
            :aria-label="t('notices.previous')"
            @click="showPreviousNotice"
          >
            <UiIcon name="arrow-right" class="size-4 rotate-180" />
          </button>
          <button
            type="button"
            class="utility-trigger utility-trigger--icon size-8 rounded-none"
            :aria-label="t('notices.next')"
            @click="showNextNotice"
          >
            <UiIcon name="arrow-right" class="size-4" />
          </button>
        </div>

        <button
          v-if="activeNotice.dismissible"
          type="button"
          class="utility-trigger utility-trigger--icon size-9 rounded-none"
          :aria-label="t('notices.dismiss')"
          @click="dismiss(activeNotice)"
        >
          <UiIcon name="x" class="size-4" />
        </button>
      </div>

      <div
        class="alert-ui flex items-start gap-4 border-border bg-white text-foreground"
        :role="activeNotice.alert ? 'alert' : 'status'"
      >
        <span
          class="flex size-10 shrink-0 items-center justify-center rounded-xl"
          :class="iconClass(activeNotice.variant)"
        >
          <UiIcon :name="iconName(activeNotice)" class="size-5" />
        </span>

        <div class="min-w-0 flex-1">
          <h2
            v-if="activeNotice.title"
            class="font-display text-base font-semibold tracking-tight"
            v-html="activeNotice.title"
          />
          <div
            class="text-sm leading-relaxed text-foreground/85"
            :class="{ 'mt-1': activeNotice.title }"
            v-html="activeNotice.message"
          />
          <p
            v-if="activeNotice.source === 'backend' && backendAvailabilityIssue?.status"
            class="mt-3 inline-flex w-fit items-center rounded-full border border-warning-border bg-warning-soft px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-warning-deep"
          >
            {{ t('system.backendUnavailable.status', { status: backendAvailabilityIssue.status }) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
